// 화면 17 — Leading Sector Timeline (Phase A) 타입.
// 권위:
//   - docs/ARCH_DASHBOARD.md v1.10 §4.17 / §6.2 (라우트 /leading/sectors, 폴링 60s, WebSocket 미사용)
//   - docs/PRD_Dashboard.md v1.1.1 §5.7
//   - dashboard/api/.../sectorhistory/dto/SectorHistoryDtos.java
//   - dashboard/api/.../sectorhistory/dto/SectorDivergenceDtos.java
//
// 주의: 백엔드 DTO 는 snake_case (@JsonProperty 명시). 다른 화면에서 쓰는 camelCase 변환 없음.
// 본 타입은 백엔드 실제 응답 그대로 1:1 매핑.
//
// 단, 응답 envelope 은 ApiResponse.ok(data, meta) — client.ts interceptor 가 data 만 언랩.
// 즉:
//   /sectors/history          → resp = List<SectorHistoryRow>  (meta { count, from, to, mode, include_sparkline } 손실)
//   /sectors/history/divergence → resp = DivergencePanel
//
// ARCH §6.2 명세는 `{rows, as_of, from, to, mode}` 래퍼를 노출하나 백엔드 구현은 List 직접 반환.
// 본 화면은 백엔드 실제 구현 (List) 를 기준으로 작성.

export type SectorHistoryMode = 'smoothed' | 'daily_top3' | 'both';

/** 한 행 — daily_sector_history 주요 11 컬럼 (ARCH_DATA §5.12). */
export interface SectorHistoryRow {
  trading_date: string;            // "YYYY-MM-DD"
  rank: number | null;             // 1..N
  sector_name: string;
  score_daily: number | null;
  is_smoothed_lead: boolean | null;
  pipeline_mode: string | null;    // today_close | next_day_pre_open
  run_id: string | null;
  // sparkline 4 컬럼 — include_sparkline=true 시에만 포함 (JsonInclude.NON_NULL)
  breadth?: number | null;
  peak?: number | null;
  avg_score?: number | null;
  stock_count?: number | null;
}

/** /sectors/history 응답 — envelope 언랩 후 List 자체. */
export type SectorHistoryResponse = SectorHistoryRow[];

/** Divergence 패널 Top3 row 1 건. */
export interface TopSectorEntry {
  rank: number;
  sector_name: string;
  score_daily: number;
}

/** /sectors/history/divergence 응답 (DivergencePanel). */
export interface DivergencePanelResponse {
  date: string;                       // "YYYY-MM-DD"
  daily_top3: TopSectorEntry[];       // 최대 3 row
  smoothed_top3: TopSectorEntry[];    // 최대 3 row
  intersection_size: number;          // 0..3
  divergence_jaccard: number | null;  // 1 - k/(6-k) ∈ {0.0, 0.5, 0.8, 1.0}
  divergence_score: number | null;    // 가중 점수차 — 0분모 케이스 null
  threshold_state: 'NORMAL' | 'WATCH' | 'ALERT';
  persistence_count: number;          // 최근 3 거래일 연속 WATCH+ 일수
  pipeline_mode: string | null;
  run_id: string | null;
}

/** 필터 — URL 쿼리스트링 ↔ 상태 동기화. */
export interface LeadingSectorsFilters {
  from: string;       // "YYYY-MM-DD"
  to: string;
  divergenceDate: string; // 격차 패널 기준 일자 (기본 = to)
}

/** 60거래일 lookback (영업일 아닌 캘린더 일수 기준 — 백엔드 KRX 캘린더 적용 위임). */
export function defaultLeadingSectorsFilters(today: Date = new Date()): LeadingSectorsFilters {
  const to = toYmd(today);
  const fromDate = new Date(today);
  // 60 영업일 ≈ 84 캘린더일 — KRX 캘린더 매핑은 백엔드 책임. UI 는 넉넉히 90일.
  fromDate.setDate(fromDate.getDate() - 90);
  return {
    from: toYmd(fromDate),
    to,
    divergenceDate: to,
  };
}

export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── 색상 매핑 (AC-DB-PRD-14 — 일관성) ────────────────
// 섹터명 → 인덱스 → 컬러 토큰. 같은 섹터는 항상 같은 색.
// 토큰: tokens.css 의 8 색을 순환. 충돌 발생 시 hash 인덱스로 분산.
export const SECTOR_COLOR_TOKENS: string[] = [
  'var(--accent)',       // 갈색
  'var(--mark-down)',    // 파랑
  'var(--ok)',           // 초록
  'var(--warn)',         // 노랑
  'var(--mark-up)',      // 빨강
  'var(--accent-ink)',   // 짙은 갈색
  'var(--danger)',       // 옥스블러드
  'var(--mark-flat)',    // 회색
];

export function sectorColor(name: string): string {
  // 단순 hash — 같은 이름이면 같은 결과.
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % SECTOR_COLOR_TOKENS.length;
  return SECTOR_COLOR_TOKENS[idx];
}
